import { filter, fromEvent, map, startWith, take, tap } from 'rxjs';
import { CHECKOUT_URL_AUTOSTART_PARAM, CheckoutDropInApi, CheckoutFrameCommunicationMessage, RedirectMessage, SetFrameVisibilityMessage } from './api';
import { ApiService } from './api/api-service';
import { CHECKOUT_DROP_IN_ENVIRONMENT_FOR_URL_ROOTS } from './environment';
import { PageLoadTypeDetector, UrlHandler } from './host-page-tools';
import { BrowserSupportService } from './host-page-tools/browser-support-service';
import { IFrameInitService, IFrameInitStyles, IFrameManager } from './iframe';

export const initCheckoutDropIn: (rootUrl: string, apiRootUrl: string) => void = (rootUrl, apiRootUrl) =>
  new CheckoutDropIn(window, document).execute(rootUrl, apiRootUrl);

export class CheckoutDropIn {
  constructor(private window: Window & typeof globalThis & { yatta?: CheckoutDropInApi }, private document: Document) {}

  private apiService: ApiService;
  private browserSupportService: BrowserSupportService;
  private iFrameManager: IFrameManager;
  private pageLoadTypeDetector: PageLoadTypeDetector;
  private urlHandler: UrlHandler;
  private iFrameInitService: IFrameInitService;

  private earlyMessagesQueueProcessed: boolean = false;
  private earlyMessagesQueue: CheckoutFrameCommunicationMessage[] = [];

  readonly CHECKOUT_FRAME_NAME = 'yatta-checkout';
  readonly CHECKOUT_DEMO_FRAME_NAME = 'yatta-checkout-demo';
  readonly CHECKOUT_DEMO_EXCLUDED_KEY = 'checkoutDemoExcluded';
  readonly CHECKOUT_INITIAL_STYLES: IFrameInitStyles = {
    zIndex: '2147483647', //32 bit max
    colorScheme: 'light',
    display: 'none',
    width: '100%'
  };

  execute(rootUrl: string, apiRootUrl: string): void {
    this.apiService = new ApiService(this.window);
    this.browserSupportService = new BrowserSupportService(this.window);
    this.iFrameManager = new IFrameManager(this.window);
    this.pageLoadTypeDetector = new PageLoadTypeDetector(this.window);
    this.urlHandler = new UrlHandler(this.window);
    this.iFrameInitService = new IFrameInitService(this.urlHandler, this.pageLoadTypeDetector);

    this.apiService.publishInterface(message => this.decideOnFrameAndHandleMessageToCheckoutFrame(message, rootUrl, apiRootUrl));

    if (this.browserSupportService.isSupported()) {
      this.addInitEventListeners(rootUrl, apiRootUrl);
    } else {
      this.addInitEventListenersIfBrowserNotSupported(rootUrl);
    }
  }

  private addInitEventListeners(rootUrl: string, apiRootUrl: string): void {
    fromEvent(this.document, 'readystatechange')
      .pipe(
        map(() => this.document.readyState),
        startWith(this.document.readyState),
        filter(readyState => readyState && readyState !== 'loading'),
        take(1),
        tap(() => this.createCheckoutFrames(rootUrl, apiRootUrl))
      )
      .subscribe();
  }

  private createCheckoutFrames(rootUrl: string, apiRootUrl: string): void {
    this.createCheckoutFrame(rootUrl, apiRootUrl);
    // on localhost, load demo checkout as well. It will later be decided, which one is openend
    if (this.window.location.hostname === 'localhost') {
      this.createCheckoutDemoFrame(rootUrl);
    }
  }

  private createCheckoutFrame(rootUrl: string, apiRootUrl: string): void {
    const checkoutUrl = CHECKOUT_DROP_IN_ENVIRONMENT_FOR_URL_ROOTS(rootUrl, apiRootUrl).webshopUrl;
    this.iFrameManager.createFrame(this.CHECKOUT_FRAME_NAME, checkoutUrl, this.CHECKOUT_INITIAL_STYLES, () => this.initCheckoutFrame(rootUrl, apiRootUrl));
    this.iFrameManager.onReceiveMessageFromFrame<CheckoutFrameCommunicationMessage>(this.CHECKOUT_FRAME_NAME, 'web-checkout', message =>
      this.handleMessageFromCheckoutFrame(message, rootUrl)
    );
  }

  private initCheckoutFrame(rootUrl: string, apiRootUrl: string): void {
    this.processEarlyMessagesQueueBeforeFrameInit(rootUrl, apiRootUrl);
    this.apiService.executeCallbacks();
    this.iFrameInitService.initFrame(CHECKOUT_URL_AUTOSTART_PARAM, message =>
      this.handleMessageToFrame(
        {
          ...message,
          source: 'checkout-drop-in'
        },
        rootUrl
      )
    );
  }

  private processEarlyMessagesQueueBeforeFrameInit(rootUrl: string, apiRootUrl: string): void {
    this.earlyMessagesQueueProcessed = true;
    this.earlyMessagesQueue.forEach(message => this.decideOnFrameAndHandleMessageToCheckoutFrame(message, rootUrl, apiRootUrl));
    this.earlyMessagesQueue = [];
  }

  private createCheckoutDemoFrame(rootUrl: string): void {
    const checkoutDemoUrl = CHECKOUT_DROP_IN_ENVIRONMENT_FOR_URL_ROOTS(rootUrl).webshopDemoUrl;
    this.iFrameManager.createFrame(this.CHECKOUT_DEMO_FRAME_NAME, checkoutDemoUrl, this.CHECKOUT_INITIAL_STYLES);
    this.iFrameManager.onReceiveMessageFromFrame<SetFrameVisibilityMessage>(this.CHECKOUT_DEMO_FRAME_NAME, 'web-checkout-demo', message =>
      this.handleSetFrameVisibilityMessage(message, rootUrl, this.CHECKOUT_DEMO_FRAME_NAME)
    );
  }

  private handleMessageFromCheckoutFrame(message: CheckoutFrameCommunicationMessage, rootUrl: string): void {
    switch (message.action) {
      case 'notifyAssetStatusChange':
        this.apiService.notifyAssetStatusChange(message.productId, message.environment, message.assetStatus);
        break;
      case 'notifySignedInChange':
        this.apiService.notifySignedInChange(message.signedIn);
        break;
      case 'notifyCheckoutSessionChange':
        this.apiService.notifyCheckoutSessionChange(message.checkoutSession);
        break;
      case 'setFrameVisibility':
        this.handleSetFrameVisibilityMessage(message, rootUrl);
        break;
      case 'redirect':
        this.handleRedirectMessage(message);
        break;
    }
  }

  private handleSetFrameVisibilityMessage(message: SetFrameVisibilityMessage, rootUrl: string, name: string = this.CHECKOUT_FRAME_NAME): void {
    if (message.action !== 'setFrameVisibility') {
      return;
    }
    if (message.visible) {
      if (name === this.CHECKOUT_FRAME_NAME) this.apiService.queueCallbacks();
      this.iFrameManager.showFrame(name);
    } else {
      this.iFrameManager.hideFrame(name);
      if (name === this.CHECKOUT_FRAME_NAME) this.apiService.executeCallbacks();
    }
    if (message.clearAutostartParam) {
      this.urlHandler.resetRelativeReference(CHECKOUT_URL_AUTOSTART_PARAM);
    }
    this.handleMessageToFrame({ source: 'checkout-drop-in', action: 'setFrameVisibilityConfirm', visible: message.visible }, rootUrl, name);
  }

  private handleRedirectMessage(message: RedirectMessage): void {
    if (message.action !== 'redirect') {
      return;
    }
    this.window.location.href = message.redirectUrl;
  }

  private decideOnFrameAndHandleMessageToCheckoutFrame(message: CheckoutFrameCommunicationMessage, rootUrl: string, apiRootUrl: string): void {
    if (!this.earlyMessagesQueueProcessed) {
      this.earlyMessagesQueue.push(message);
      return;
    }

    if (message.action === 'startCheckout' && !message.params?.manageContract) {
      const hostIsLocalhost = this.window.location.hostname === 'localhost';

      let checkoutDemoExcludedValue: string | null;
      let localStorageSupported: boolean = true;
      try {
        checkoutDemoExcludedValue = this.window.localStorage.getItem(this.CHECKOUT_DEMO_EXCLUDED_KEY);
      } catch (e) {
        localStorageSupported = false;
        checkoutDemoExcludedValue = null;
      }

      const checkoutDemoExcludedArray = checkoutDemoExcludedValue ? (JSON.parse(checkoutDemoExcludedValue) as string[]) : [];
      const url =
        CHECKOUT_DROP_IN_ENVIRONMENT_FOR_URL_ROOTS(rootUrl, apiRootUrl).apiUrl + `/checkout/webshop-demo/check-license-id-type?licenseId=${message.productId}`;
      if (hostIsLocalhost && !checkoutDemoExcludedArray.includes(message.productId)) {
        fetch(url)
          .then(res => {
            if (!res.ok) {
              const err = new Error('HTTP status code: ' + res.status);
              throw err;
            }
            return res.json();
          })
          .then(data => {
            if (data.demoMode === false && localStorageSupported) {
              this.window.localStorage.setItem(this.CHECKOUT_DEMO_EXCLUDED_KEY, JSON.stringify(checkoutDemoExcludedArray.concat([message.productId])));
            }
            this.handleMessageToFrame(message, rootUrl, data.demoMode ? this.CHECKOUT_DEMO_FRAME_NAME : this.CHECKOUT_FRAME_NAME);
          })
          .catch(() =>
            this.handleMessageToFrame(
              {
                source: 'checkout-drop-in',
                action: 'showError',
                errorType: 'SOLUTION_ID_DEMO_CHECK_FAILED'
              },
              rootUrl
            )
          );
        return;
      }
    }

    this.handleMessageToFrame(message, rootUrl);
  }

  private handleMessageToFrame(message: CheckoutFrameCommunicationMessage, rootUrl: string, name: string = this.CHECKOUT_FRAME_NAME): void {
    if (!this.browserSupportService.isSupported()) {
      this.browserSupportService.showNotSupportedWindow(CHECKOUT_DROP_IN_ENVIRONMENT_FOR_URL_ROOTS(rootUrl).staticPagesUrl);
      return;
    }

    this.iFrameManager.sendMessageToFrame(name, message);
  }

  private addInitEventListenersIfBrowserNotSupported(rootUrl: string): void {
    this.window.addEventListener('load', () => {
      let showNotSupportedWindow: boolean = false;
      if (this.urlHandler.parseQuery(CHECKOUT_URL_AUTOSTART_PARAM)) {
        showNotSupportedWindow = true;
        this.urlHandler.resetRelativeReference(CHECKOUT_URL_AUTOSTART_PARAM);
      }
      if (showNotSupportedWindow) {
        this.browserSupportService.showNotSupportedWindow(CHECKOUT_DROP_IN_ENVIRONMENT_FOR_URL_ROOTS(rootUrl).staticPagesUrl);
      }
    });
  }
}
